if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      this.mainForm = this.productSection.querySelector('.js-product-form-main');
      this.addBtn = this.querySelector('[data-sticky-atc-add]');
      this.selectionEl = this.querySelector('[data-sticky-atc-selection]');
      this.changeLink = this.querySelector('[data-sticky-atc-change]');
      this.withEl = this.querySelector('[data-sticky-atc-with]');
      this.priceCurrent = this.querySelector('.price__current .js-value');
      this.priceWas = this.querySelector('.price__was');

      this.bindEvents();

      if (this.addBtn && this.mainForm) this.bindAddButton();
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.onBundleState) document.removeEventListener('on:rgbo:state', this.onBundleState);
    }

    bindEvents() {
      // Position-based, not IntersectionObserver: a fast scroll or a jump (anchor link, fling)
      // can carry the buy box from "below the screen" to "above the screen" between frames,
      // and the observer then never fires, leaving the panel hidden for the rest of the page.
      // The stock theme also hid the panel within 100px of the page end; Revgear wants it
      // available all the way down.
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => { this.raf = null; this.updateVisibility(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.updateVisibility();
    }

    updateVisibility() {
      let out = true;
      if (theme.mediaMatches.md) {
        // Desktop: hide while the (sticky) product info column is on screen.
        if (this.productInfo) {
          const r = this.productInfo.getBoundingClientRect();
          out = r.bottom > 0 && r.top < window.innerHeight;
        }
      } else if (this.productForm) {
        // Phone (GH #231, 2026-10-29): the bar is on screen from first paint and stays for the whole
        // page, so Add to cart is always one tap away.
        out = false;
      }
      this.classList.toggle('sticky-atc-panel--out', out);
    }

    get mainAddBtn() {
      return this.mainForm.querySelector('[name="add"]');
    }

    get picker() {
      return this.productSection.querySelector('variant-picker');
    }

    /**
     * The panel adds THIS product only, so its state comes from the variant picker, never from
     * the main button's label: the bundle offers script relabels that button ("Choose options
     * for 2 items") whenever a set is selected below, which means nothing up here.
     */
    bindAddButton() {
      this.addBtn.addEventListener('click', this.handleAddClick.bind(this));

      this.productSection.addEventListener('on:variant:change', (evt) => {
        this.syncState(evt.detail.variant, evt.detail.selectedOptions);
      });

      // "Buy it as a set" block (rg-bundle-offers): the panel follows whatever the buy box
      // would add. The bridge announces on every change; on construction read it directly,
      // because the theme re-renders this element on each variant change.
      this.bundleRoot = document.querySelector('.rgbo--inline');
      this.bundleState = this.bundleRoot?.rgboPanel ? this.bundleRoot.rgboPanel.state() : null;
      this.onBundleState = (evt) => { this.bundleState = evt.detail; this.render(); };
      document.addEventListener('on:rgbo:state', this.onBundleState);

      if (this.changeLink) {
        this.changeLink.addEventListener('click', (evt) => { evt.preventDefault(); this.scrollToPicker(); });
      }

      const init = () => {
        const picker = this.picker;
        if (picker && typeof picker.getSelectedOptions === 'function') {
          this.syncState(picker.variant, picker.getSelectedOptions());
        } else {
          this.syncState(this.currentVariantFromForm(), []);
        }
      };
      if (customElements.get('variant-picker')) init();
      else customElements.whenDefined('variant-picker').then(init);
    }

    currentVariantFromForm() {
      const id = parseInt(this.mainForm.querySelector('[name="id"]')?.value, 10);
      return id ? { id, available: !this.mainAddBtn?.disabled, title: '' } : null;
    }

    /** Names of option groups with nothing chosen yet, e.g. ["Size"]. */
    unchosenOptionNames(selectedOptions) {
      const picker = this.picker;
      if (!picker) return [];
      const selectors = Array.from(picker.querySelectorAll('.option-selector:not(.option-selector--custom)'));
      return selectedOptions
        .map((value, i) => (value === null ? selectors[i]?.dataset.option || 'options' : null))
        .filter(Boolean);
    }

    syncState(variant, selectedOptions) {
      this.variant = variant;
      this.selectedOptions = selectedOptions || [];
      this.render();
    }

    render() {
      const variant = this.variant;
      const selectedOptions = this.selectedOptions || [];
      const chosen = selectedOptions.filter(Boolean);
      const bs = this.bundleState;
      const set = bs && bs.mode === 'set' ? bs : null;
      // Ticked "People also like" add-ons ride along with either choice.
      const addons = bs && bs.addons && bs.addons.count > 0 ? bs : null;

      this.classList.toggle('sticky-atc-panel--set', !!set);
      const esc = (t) => String(t).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
      // Line 1: what was picked for THIS product ("Blue/Black · 12 oz"), in bold.
      if (this.selectionEl) this.selectionEl.innerHTML = chosen.map((v) => `<b>${esc(v)}</b>`).join(' · ');
      // Line 2: what rides along — each companion (regular) with its chosen size (bold). A
      // companion still needing a size shows its name alone: the button says what to do next,
      // and text in this list is not clickable so it must not look like a prompt.
      if (this.withEl) {
        const parts = [];
        if (set && set.companions && set.companions.length) {
          parts.push(...set.companions.map((c) => (c.variant ? `${esc(c.name)}: <b>${esc(c.variant)}</b>` : esc(c.name))));
        } else if (set && set.setItems) {
          parts.push(`${set.setItems} more`);
        }
        if (addons) parts.push(esc(addons.addonNote.replace(/^\s*\+\s*/, '')));
        // One item per line, each led by "+", so names and sizes never run together.
        this.withEl.innerHTML = parts.map((part) => `<span class="sticky-atc-panel__with-item">+ ${part}</span>`).join('');
      }
      this.renderPrice(set || addons);

      // An unchosen option on THIS product outranks everything: nothing can be added without it.
      const missing = this.unchosenOptionNames(selectedOptions);
      if (missing.length) {
        this.setMode('select', `Choose ${missing.join(' & ')}`, false);
        return;
      }

      if (!variant) {
        this.setMode('unavailable', theme.strings.noVariant || 'Unavailable', true);
        return;
      }

      if (!variant.available) {
        // Sold out → let the main button's back-in-stock proxy handle it (the bundle
        // script leaves that state alone), mirroring its label.
        const main = this.mainAddBtn;
        this.setMode('bis', main ? main.textContent.trim() : theme.strings.noStock, false);
        return;
      }

      if (set) {
        if (set.missing.length) {
          const who = set.missing.length === 1 ? set.missing[0] : `${set.missing.length} items`;
          this.setMode('set-select', `Choose options for ${who}`, false);
        } else {
          this.setMode('set-add', addons ? `Add ${set.itemCount} items to cart` : 'Add set to cart', false);
        }
        return;
      }

      if (addons) {
        // This item plus add-ons: the bundle script adds them together, same as the main button.
        this.setMode('set-add', addons.itemCount === 2 ? 'Add both to cart' : `Add ${addons.itemCount} items to cart`, false);
        return;
      }

      this.setMode('add', this.addBtn.dataset.addToCartText, false);
    }

    /** Set chosen below → show the set total (and struck "was") instead of this item's price. */
    renderPrice(set) {
      if (!this.priceCurrent) return;
      if (this.priceCurrent.dataset.ownHtml === undefined) {
        this.priceCurrent.dataset.ownHtml = this.priceCurrent.innerHTML;
        if (this.priceWas) this.priceWas.dataset.ownHtml = this.priceWas.innerHTML;
      }
      if (set) {
        this.priceCurrent.innerHTML = set.totalHtml || this.priceCurrent.dataset.ownHtml;
        if (this.priceWas) {
          const value = this.priceWas.querySelector('.js-value');
          if (value) value.innerHTML = set.wasHtml || '';
          this.priceWas.hidden = !set.wasHtml;
        }
      } else {
        this.priceCurrent.innerHTML = this.priceCurrent.dataset.ownHtml;
        if (this.priceWas) {
          this.priceWas.innerHTML = this.priceWas.dataset.ownHtml;
          this.priceWas.hidden = false;
        }
      }
    }

    scrollToPicker() {
      const anchor = document.getElementById(this.addBtn.dataset.anchor)
        || this.productSection.querySelector('.product-options');
      if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const firstOption = this.productSection.querySelector('.product-options input:not(:checked), .product-options select, .product-options button');
      if (firstOption) setTimeout(() => firstOption.focus({ preventScroll: true }), 400);
    }

    setMode(mode, text, disabled) {
      this.addBtn.dataset.mode = mode;
      this.addBtn.textContent = text;
      this.addBtn.disabled = disabled;
      const choose = mode === 'select' || mode === 'set-select';
      this.addBtn.classList.toggle('sticky-atc-panel__add--choose', choose);
      this.addBtn.classList.toggle('btn--secondary', choose);
      this.addBtn.classList.toggle('btn--primary', !choose);
    }

    handleAddClick(evt) {
      evt.preventDefault();
      const mode = this.addBtn.dataset.mode;
      if (this.addBtn.classList.contains('is-loading')) return;

      if (mode === 'select') { this.scrollToPicker(); return; }

      if (mode === 'bis') { this.mainAddBtn?.click(); return; }

      if (mode === 'set-select') { this.bundleRoot?.rgboPanel?.focusMissing(); return; }

      if (mode === 'set-add') {
        const bridge = this.bundleRoot?.rgboPanel;
        if (!bridge) return;
        this.addBtn.classList.add('is-loading');
        this.addBtn.setAttribute('aria-busy', 'true');
        bridge.add(() => {
          this.addBtn.classList.remove('is-loading');
          this.addBtn.removeAttribute('aria-busy');
        });
        return;
      }

      if (mode !== 'add' || !this.variant) return;
      this.addToCart(this.variant.id);
    }

    /** Adds one line: this product's chosen variant, at the buy-box quantity. */
    async addToCart(variantId) {
      const qtyInput = this.mainForm.querySelector('[name="quantity"]');
      const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);
      const cartDrawer = document.querySelector('cart-drawer');

      let sections = 'cart-icon-bubble';
      if (cartDrawer) sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;

      // rg-saved-2026-10-16: price-at-add `_rg_p` (GH #175 §3.2), identical to what the buy-box
      // form would send so the two paths merge into one cart line. The buy-box input exists only
      // while the wishlist switch is on; without it no property is added.
      const item = { id: variantId, quantity };
      const rgP = this.mainForm.querySelector('input[name="properties[_rg_p]"]');
      if (rgP && rgP.value) {
        const rgPrice = this.variant && this.variant.id === variantId ? this.variant.price : null;
        item.properties = {
          _rg_p: Number.isInteger(rgPrice) ? `${rgP.value.split(':')[0]}:${rgPrice}` : rgP.value
        };
      }

      this.addBtn.classList.add('is-loading');
      this.addBtn.setAttribute('aria-busy', 'true');

      try {
        let data;
        if (item.properties && window.rgSaved && window.rgSaved.cart) {
          // rg-saved-2026-10-17 (GH #175 §3.2 repeat-add protocol): an existing line of this
          // variant with a different `_rg_p` is bumped by key + quantity instead of split.
          const res = await window.rgSaved.cart.add([item], sections);
          if (!res.ok) throw new Error(res.message || theme.strings.cartError);
          data = res.data;
        } else {
          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [item], sections, sections_url: window.location.pathname })
          });
          data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || response.status);
        }

        if (theme.settings.vibrateOnATC && window.navigator.vibrate) window.navigator.vibrate(100);

        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }

        const ProductForm = customElements.get('product-form');
        if (ProductForm && typeof ProductForm.updateCartIcon === 'function') ProductForm.updateCartIcon(data);

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.pendingOpener = this.addBtn;
          cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        } else if (window.location.pathname === theme.routes.cart) {
          window.location.reload();
        }
      } catch (error) {
        const errorBox = this.mainForm.querySelector('.js-form-error');
        if (errorBox) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
          errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.addBtn.removeAttribute('aria-busy');
      }
    }

  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
